import { ChangeDetectorRef, Component, HostListener, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DashboardApiService } from '../../../services/dashboard-api.service';
import { OverviewCardComponent } from '../../../../../shared/charts/components/elements/overview-card/overview-card.component';
import { LineChartComponent } from '../../../../../shared/charts/components/elements/line-chart/line-chart.component';
import { BarChartComponent } from '../../../../../shared/charts/components/elements/bar-chart/bar-chart.component';
import { PieChartComponent } from '../../../../../shared/charts/components/elements/pie-chart/pie-chart.component';
import { LimitOffsetPagination } from '../../../../../shared/models/limit-offset-pagination';
import { PaginatedVendorReviews } from '../../../../../shared/models/paginated-vendor-reviews';
import { DateRangePickerInputComponent, DateRange } from '../../../../../shared/date-picker';
import { RatingCount } from '../../../../../shared/models/rating-count';
import { AgeGroupCount } from '../../../../../shared/models/age-group-count';
import { SalesSeries, SalesGranularity } from '../../../../../shared/models/sales-point';
import { OrderStats } from '../../../../../shared/models/order-stats';
import { TopProduct } from '../../../../../shared/models/top-product';
import { LowStockItem } from '../../../../../shared/models/low-stock-item';
import { GenericPaginationResponseBody } from '../../../../../shared/models/Generic-pagination-response-body';
import { WalletApiService } from '../../../../wallet/services/wallet-api.service';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { OrdersApiService } from '../../../../orders/services/orders-api.service';
import { GetOrdersResponse } from '../../../../orders/models/get-orders-response';
import { TablePaginationComponent } from "../../../../../shared/components/table-pagination/table-pagination.component";
import { OverlayModule } from "@angular/cdk/overlay";
import { forkJoin } from 'rxjs';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterLink } from "@angular/router";

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, FormsModule, OverviewCardComponent, LineChartComponent, BarChartComponent, PieChartComponent, DateRangePickerInputComponent, TablePaginationComponent, OverlayModule, TranslateModule, RouterLink],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit {

  sectionRanges: {
    salesByCategoryA: DateRange | null;
    salesByCategoryB: DateRange | null;
    salesByCategoryBar: DateRange | null;
    ratings: DateRange | null;
    inventory: DateRange | null;
  } = {
    salesByCategoryA: null,
    salesByCategoryB: null,
    salesByCategoryBar: null,
    ratings: null,
    inventory: null,
  };

  cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  apiService = inject(DashboardApiService);
  ordersApiService = inject(OrdersApiService);
  walletApiService = inject(WalletApiService);
  toastNotificationService = inject(ToastNotificationService);
  languageService = inject(LanguageService);
  translate = inject(TranslateService);

  vendorReviewsPagination: LimitOffsetPagination = { limit: 5, offset: 0 };
  vendorReviews: PaginatedVendorReviews | undefined;

  ordersResponse: GetOrdersResponse | undefined;
  ordersPagination: LimitOffsetPagination = { limit: 4, offset: 0 };


  movePieChart = false;
  ratingChartLabels = ['0⭐', '1⭐', '2⭐', '3⭐', '4⭐', '5⭐'];
  reviewsCounts: RatingCount[] | undefined;

  ratingChartData: any | undefined = undefined;
  dashboardLoaded = false;

  salesChartLabels: string[] = this.buildLastSixMonthsLabels();
  salesChartData: any[] = [];

  ageGroups = [
    "0-18",
    "18-24",
    "25-34",
    "35-44",
    "45+"
  ];

  ageChartData = [
    { label: "0-18", value: 0, color: "#E8F1FB" },
    { label: "18-24", value: 0, color: "#A8C7EC" },
    { label: "25-34", value: 0, color: "#5894D8" },
    { label: "35-44", value: 0, color: "#1F63B5" },
    { label: "45+", value: 0, color: "#0A2244" }
  ];

  orderStats = {
    delivered: 0,
    outForDelivery: 0,
    preparing: 0,
    total: 0
  };

  paymentStats = {
    received_payments: 0,
    pending_payments: 0,
    total_sales: 0
  };

  topProducts: TopProduct[] = [];

  lowStockResponse: GenericPaginationResponseBody<LowStockItem> | undefined;
  lowStockPagination: LimitOffsetPagination = { limit: 5, offset: 0 };

  ngOnInit(): void {
    this.updateChartVisibility(window.innerWidth);
    this.sectionRanges.salesByCategoryB = this.thisWeekRange();
    this.salesChartData = this.buildSalesDataset([0, 0, 0, 0, 0, 0]);
    this.loadDashboard();
  }

  loadDashboard(): void {
  this.dashboardLoaded = false;

  forkJoin({
    reviews: this.apiService.getVendorReviews(
      this.vendorReviewsPagination.limit,
      this.vendorReviewsPagination.offset
    ),

    orders: this.ordersApiService.getOrders(
      this.ordersPagination
    ),

    summary: this.apiService.getVendorReviewsSummary(),

    ageGroups: this.apiService.getVendorCustomerAgeGroups(this.sectionRanges.salesByCategoryA),

    sales: this.apiService.getVendorSalesSeries(this.sectionRanges.salesByCategoryB),

    wallet: this.walletApiService.getWallet(),

    orderStats: this.apiService.getVendorOrderStats(),

    topProducts: this.apiService.getVendorTopProducts(),

    lowStock: this.apiService.getVendorLowStock(this.lowStockPagination)
  }).subscribe({
    next: ({ reviews, orders, summary, ageGroups, sales, wallet, orderStats, topProducts, lowStock }) => {

      this.vendorReviews = reviews;

      this.ordersResponse = orders;

      this.paymentStats = {
        received_payments: wallet.available_balance,
        pending_payments: wallet.pending_balance,
        total_sales: wallet.total_sales,
      };
      this.applyOrderStats(orderStats);

      this.topProducts = topProducts;

      this.lowStockResponse = lowStock;

      this.applyAgeGroups(ageGroups);

      this.applySalesSeries(sales);

      this.reviewsCounts = summary;

      const { ratingChartLabels, ratingsChartData } = this.formatRatingsChart(summary);
      this.ratingChartLabels = ratingChartLabels;

      this.ratingChartData = [{
        label: this.translate.instant('dashboard.chart_labels.reviews'),
        data: ratingsChartData,
        backgroundColor: [
          '#FFF192',
          '#FFEA61',
          '#FFEA61',
          '#FFDD3C',
          '#FFDD3C'
        ],
        borderRadius: 7,
        barThickness: 20
      }];

      this.dashboardLoaded = true;
    }, error: () => {
      this.dashboardLoaded = true;
        this.toastNotificationService.createNotification(this.translate.instant('dashboard.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('dashboard.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
    }
  });
}

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.updateChartVisibility(event.target.innerWidth);
  }

  getOrders() {
    this.ordersApiService.getOrders(this.ordersPagination).subscribe({
      next: orders => {
        this.ordersResponse = orders;
      },
      error: error => {
        this.toastNotificationService.createNotification(this.translate.instant('dashboard.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('dashboard.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  applyAgeGroups(data: AgeGroupCount[]): void {
    const counts = new Map(data.map(d => [d.age_group, d.count]));
    // Keep the existing labels/colors; only refresh the values (new array reference
    // so the pie chart's ngOnChanges fires and redraws).
    this.ageChartData = this.ageChartData.map(item => ({
      ...item,
      value: counts.get(item.label) ?? 0,
    }));
  }

  onAgeRangeChange(range: DateRange | null): void {
    this.sectionRanges.salesByCategoryA = range;
    this.apiService.getVendorCustomerAgeGroups(range).subscribe({
      next: (data) => this.applyAgeGroups(data),
      error: () => {
        this.toastNotificationService.createNotification(this.translate.instant('dashboard.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('dashboard.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  applySalesSeries(series: SalesSeries): void {
    this.salesChartLabels = series.points.map(point => this.formatSalesLabel(point.date, series.granularity));
    this.salesChartData = this.buildSalesDataset(series.points.map(point => point.total));
  }

  onSalesRangeChange(range: DateRange | null): void {
    this.sectionRanges.salesByCategoryB = range;
    this.apiService.getVendorSalesSeries(range).subscribe({
      next: (data) => this.applySalesSeries(data),
      error: () => {
        this.toastNotificationService.createNotification(this.translate.instant('dashboard.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('dashboard.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  private buildSalesDataset(data: number[]): any[] {
    return [
      {
        label: this.translate.instant('dashboard.chart_labels.sales'),
        data,
        borderColor: '#16BDCA',
        borderWidth: 2,
        pointRadius: 2.5,
        pointHoverRadius: 5,
        pointBackgroundColor: '#16BDCA',
        pointBorderColor: '#16BDCA',
        pointHoverBackgroundColor: '#16BDCA',
        pointHoverBorderColor: '#16BDCA',
        pointBorderWidth: 1.5,
        tension: 0.4,
      },
    ];
  }

  private formatSalesLabel(iso: string, granularity: SalesGranularity): string {
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const lang = this.languageService.currentLang();
    switch (granularity) {
      case 'year':
        return date.toLocaleDateString(lang, { year: 'numeric' });
      case 'month':
        return date.toLocaleDateString(lang, { month: 'short', year: '2-digit' });
      default: // day & week — labelled by the bucket's start date
        return date.toLocaleDateString(lang, { day: 'numeric', month: 'short' });
    }
  }

  thisWeekRange(): DateRange {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekday = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (weekday === 0 ? 6 : weekday - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: this.toIsoDate(monday), end: this.toIsoDate(sunday) };
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getVendorReviews(): void {
    this.apiService.getVendorReviews(this.vendorReviewsPagination.limit, this.vendorReviewsPagination.offset).subscribe((response: PaginatedVendorReviews) => {
      this.vendorReviews = response;
    });
  }

  getVendorReviewsSummary(): void {
    this.apiService.getVendorReviewsSummary().subscribe({
      next: (response) => {
        this.reviewsCounts = response;

        const { ratingChartLabels, ratingsChartData } = this.formatRatingsChart(response);

        this.ratingChartLabels = ratingChartLabels;

        this.ratingChartData = [
          {
            label: this.translate.instant('dashboard.chart_labels.reviews'),
            data: ratingsChartData,
            backgroundColor: ['#FFF192', '#FFEA61', '#FFEA61', "#FFDD3C", "#FFDD3C"],
            borderRadius: 7,
            barThickness: 20,
          }
        ]
      },
      error: (err) => {
        this.toastNotificationService.createNotification(this.translate.instant('dashboard.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('dashboard.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
      }
    })
  }

  updateChartVisibility(width: number): void {
    const shouldShow = width < 1280; // or any threshold you want
    if (this.movePieChart !== shouldShow) {
      this.movePieChart = shouldShow;
      this.cdRef.detectChanges(); // prevent ExpressionChangedAfterItHasBeenCheckedError
    }
  }

  getReviewBackgroundColor(id: number): string {
    const colors = ['#FCC5F3', '#2DCA72', '#EBEBEF'];
    return colors[id % colors.length];
  }

  buildLastSixMonthsLabels(): string[] {
    const labels: string[] = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      labels.push(d.toLocaleDateString(this.languageService.currentLang(), { month: 'short' }));
    }
    return labels;
  }

  formatRatingsChart(data: RatingCount[]) {
    const sorted = [...data].sort((a, b) => a.rating - b.rating);

    const ratingChartLabels = sorted.map(item => `${item.rating}⭐`);
    const ratingsChartData = sorted.map(item => item.count);

    return { ratingChartLabels, ratingsChartData };
  }

  updateOrdersPagination(pagination: LimitOffsetPagination) {
    this.ordersPagination = pagination;
    this.getOrders();
  }

  updateLowStockPagination(pagination: LimitOffsetPagination) {
    this.lowStockPagination = pagination;
    this.getLowStock();
  }

  lowStockVariantLabel(item: LowStockItem): string {
    const isAr = this.languageService.currentLang() === 'ar';
    const product = isAr ? item.product_name_ar : item.product_name_en;
    const variant = isAr ? item.variant_name_ar : item.variant_name_en;
    // Default/single variants share the product's name — don't repeat it.
    return variant && variant !== product ? variant : '';
  }

  getLowStock() {
    this.apiService.getVendorLowStock(this.lowStockPagination).subscribe({
      next: response => {
        this.lowStockResponse = response;
      },
      error: () => {
        this.toastNotificationService.createNotification(this.translate.instant('dashboard.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('dashboard.errors.load_failed_title'), ToastPosition.TOP_CENTER);
      }
    });
  }

  applyOrderStats(stats: OrderStats): void {
    this.orderStats = {
      outForDelivery: stats.out_for_delivery,
      delivered: stats.delivered,
      preparing: stats.preparing,
      total: stats.total
    };
  }

  getOrderStatusKey(status: string) {
    switch (status) {
      case 'placed':
      case 'preparing_order':
      case 'ready_for_pickup':
      case 'out_for_delivery':
      case 'delivered':
      case 'cancelled':
      case 'refunded':
        return `dashboard.order_status.${status}`;
      default:
        return 'dashboard.order_status.unknown';
    }
  }

}
